import { KPIs, KPIsRaw } from "./types/kpis";
import { fetchWithRetry } from "../client";

const DEFAULT_KPI_NAMES = [
  "flight_all",
  "pax_all",
  "on_time_a",
  "on_time_b",
  "on_time_c",
  "on_time_d",
  "Avg_flight_day",
  "flight_cargo",
  "distinct_airline_all",
  "distinct_airport_all",
  "flight_international",
  "flight_domestic",
  "flight_night",
  "flight_day",
] as const;

/**
 * KPIs de la operación de carga que consume `/carga`. El orden importa: el
 * primero es la cifra principal de la sección y los tres `cargo_*_tm` forman
 * la parte-todo (total = cargueros + belly), así que se piden juntos.
 */
export const CARGO_KPI_NAMES = [
  "cargo_all_tm",
  "cargo_freight_tm",
  "cargo_belly_tm",
  "flight_cargo",
  "distinct_airline_cargo",
  "distinct_airport_cargo",
  "distinct_country_cargo",
] as const;

function parseNumber(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeKpiName(value: string): string {
  return value.trim().toLowerCase();
}

export async function fetchKPIs(
  kpiNames: readonly string[] = DEFAULT_KPI_NAMES,
): Promise<KPIs[]> {
  const res = await fetchWithRetry<{ data: KPIsRaw[] }>(
    `/stats/kpis?per_page=100`,
  );

  const allow = new Set(kpiNames.map(normalizeKpiName));
  const filtered = res.data.filter((item) =>
    allow.has(normalizeKpiName(item.kpi_name)),
  );

  const latestByName = new Map<string, KPIsRaw>();
  for (const item of filtered) {
    const key = normalizeKpiName(item.kpi_name);
    const prev = latestByName.get(key);
    if (!prev || item.event_date > prev.event_date) {
      latestByName.set(key, item);
    }
  }

  return kpiNames
    .map((name) => latestByName.get(normalizeKpiName(name)))
    .filter((item): item is KPIsRaw => Boolean(item))
    .map(
      (item): KPIs => ({
        site_id: item.site_id,
        event_date: item.event_date,
        kpi_id: item.kpi_id,
        kpi_name: item.kpi_name,
        kpi_description: item.kpi_description,
        kpi_value: parseNumber(item.kpi_value),
        variation_pct: parseNumber(item.variation_pct),
        insert_date: item.insert_date,
      }),
    );
}
